import React, {useCallback, useEffect, useState, useMemo} from 'react'
import moment from 'moment-timezone'
import Select, {MultiValue, ActionMeta} from 'react-select'
import DatePicker from 'react-datepicker'
import {AdminReportFilter} from '../../../../services/cellcast.v2.types'
import {useLoadedData} from '../../../hooks/useGetLoadedData'
import {
  useAdminReportUserListQuery,
  useLazyAdminReportActivityLogQuery,
} from '../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import {
  MessageDeliveryOptions,
  REQUEST_FROM,
  REQUEST_FORM_TYPE,
} from '../../UserPages/reports-analytics/Common'
import {KTSVG} from '../../../../_metronic/helpers'
import {User} from '../../../../services/cellcast.v3.types'
import styles from './AdminActivityLogStyles.module.scss'
import 'react-datepicker/dist/react-datepicker.css'
import ToggleSwitch from 'react-bootstrap/Form'
import { useGetGatewayProviderQuery } from '../../../../services/cellcast'

// Define option type for select dropdowns
interface SelectOption {
  label: string
  value: string
}

// Define FilterState interface for the enhanced filters
interface FilterState {
  sourceDestination: string
  userIds: Array<SelectOption>
  gateway: Array<SelectOption>
  messageStatus: Array<SelectOption>
  messageSource: Array<SelectOption>
  messageContent: string
  dateRange: [Date | null, Date | null]
  includeContent: boolean
}

// Define timeframe options
interface TimeframeOption {
  label: string
  value: string
  hours?: number
  days?: number
  isCustom?: boolean
  queryParam?: string
}

// Define active filters state
interface ActiveFilters {
  users: SelectOption[]
  statuses: SelectOption[]
  gateways: SelectOption[]
  sources: SelectOption[]
  dateRange: [Date | null, Date | null]
  searchText: string
  selectedTimeframe?: TimeframeOption
}

// Predefined timeframe options
const timeframeOptions: TimeframeOption[] = [
  {label: 'Last 1 Hour', value: 'last_1_hour', hours: 1},
  {label: 'Last 2 Hours', value: 'last_2_hours', hours: 2},
  {label: 'Last 6 Hours', value: 'last_6_hours', hours: 6},
  {label: 'Last 12 Hours', value: 'last_12_hours', hours: 12},
  {label: 'Last 24 Hours', value: 'last_24_hours', hours: 24},
  {label: 'Last 2 Days', value: 'last_2_days', days: 2},
  {label: 'Last 3 Days', value: 'last_3_days', days: 3},
  {label: 'Last 7 Days', value: 'last_7_days', days: 7},
  {label: 'Last 14 Days', value: 'last_14_days', days: 14},
  {label: 'Last 30 Days', value: 'last_30_days', days: 30},
  {label: 'Custom Range', value: 'custom', isCustom: true},
]

const AdminActivityLogsPage = () => {
  const {calculate, loadedData, setLoadedData} = useLoadedData()
    const {data: provider} = useGetGatewayProviderQuery(null)
    
  const today = new Date()
  const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)

  const [params, setParams] = useState<AdminReportFilter>({
    page: 1,
    limit: 10,
    from: moment(sevenDaysAgo).format('YYYY-MM-DD'),
    to: moment(today).format('YYYY-MM-DD'),
    requestFrom: REQUEST_FROM.filter((it) => it.value !== REQUEST_FORM_TYPE.BULK)
      .map((it) => it.value)
      .join(','),
    sourceDestination: '',
    timeFrame: 'last_1_hour',
  })

  const [triggerFetch, setTriggerFetch] = useState(false)

  // Active filters state for React Select
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    users: [],
    statuses: [],
    gateways: [],
    sources: [],
    dateRange: [today, today],
    searchText: '',
    selectedTimeframe:
      timeframeOptions.find((t) => t.value === 'last_1_hour') || timeframeOptions.at(0), // Default to last_1_hour
  })

  // State for custom date picker visibility
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false)

  // State for content visibility
  const [showContent, setShowContent] = useState(false)

  const [fetch, {isLoading, data, isFetching}] = useLazyAdminReportActivityLogQuery()
  const [fetchTotal, {data: dataTotal}] = useLazyAdminReportActivityLogQuery()
  const {data: userData} = useAdminReportUserListQuery(null)

  // Define callback functions first
  const doFetch = useCallback(
    (fetchParams: AdminReportFilter) => {
      fetch(fetchParams)
        .then((res) => {
          if ('error' in res) {
            toast.error(res.error as string)
          }
        })
        .catch((err) => {
          toast.error(err.message || 'Failed to fetch data')
        })
    },
    [fetch]
  )

  const doFetchTotal = useCallback(
    (fetchParams: AdminReportFilter) => {
      fetchTotal(fetchParams)
        .then((res) => {
          if ('error' in res) {
            toast.error(res.error as string)
            console.error('Error fetch message total:', res.error)
          }
        })
        .catch((err) => {
          toast.error(err.message || 'Failed to fetch total data')
        })
    },
    [fetchTotal]
  )

  // Handle triggered fetches
  useEffect(() => {
    if (triggerFetch) {
      doFetch(params)
      doFetchTotal({...params, groupBy: null})
      setTriggerFetch(false)
    }
  }, [triggerFetch, params, doFetch, doFetchTotal])

  // Handle triggered fetches
  useEffect(() => {
    if (triggerFetch) {
      doFetch(params)
      doFetchTotal({...params, groupBy: null})
      setTriggerFetch(false)
    }
  }, [triggerFetch, params, doFetch, doFetchTotal])

  // Update loaded data when data changes
  useEffect(() => {
    if (data) {
      const calculatedData = calculate(data.data.current || 1, params.limit, data.data.total || 0)
      setLoadedData(calculatedData)
    }
  }, [data, params.limit, calculate, setLoadedData])

    const providerList = provider?.data?.map((it: any) => ({
      value: it?.gatewayConfig?.name,
      label: `${it?.DB}-${it?.gatewayName}`,
      type: it?.type?.toLowerCase(),
    }))

  // Filter options for the enhanced filters
  const filterOptions = useMemo(
    () => ({
      userOptions: (userData?.data ?? []).map((user: User) => ({
        label: user.email,
        value: user._id,
      })),
      gatewayOptions: providerList,
      statusOptions: MessageDeliveryOptions.filter((it) => it.value !== ''),
      sourceOptions: REQUEST_FROM,
    }),
    [userData, providerList]
  )

  // Custom styles for React Select
  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: '44px',
      border: '1px solid #e4e6ea',
      borderRadius: '8px',
      backgroundColor: '#f5f8fa',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(114, 57, 234, 0.25)' : 'none',
      borderColor: state.isFocused ? '#7239ea' : '#e4e6ea',
      '&:hover': {
        borderColor: '#7239ea',
      },
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: '#f8f5ff',
      borderRadius: '16px',
      border: '1px solid #e4e6ea',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#7239ea',
      fontWeight: '500',
      fontSize: '0.875rem',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#7239ea',
      borderRadius: '0 16px 16px 0',
      '&:hover': {
        backgroundColor: '#7239ea',
        color: 'white',
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#a1a5b7',
      fontStyle: 'italic',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#7239ea' : state.isFocused ? '#f8f5ff' : 'white',
      color: state.isSelected ? 'white' : '#5e6278',
      '&:hover': {
        backgroundColor: state.isSelected ? '#7239ea' : '#f8f5ff',
        color: state.isSelected ? 'white' : '#7239ea',
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e4e6ea',
    }),
  }

  // Handle multi-select changes
  const handleMultiSelectChange = (
    selectedOptions: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>,
    filterType: keyof Pick<ActiveFilters, 'users' | 'statuses' | 'gateways' | 'sources'>
  ) => {
    const newOptions = selectedOptions ? Array.from(selectedOptions) : []

    setActiveFilters((prev) => ({
      ...prev,
      [filterType]: newOptions,
    }))

    // Update params based on filter type
    const newParams = {...params, page: 1}

    switch (filterType) {
      case 'users':
        newParams.userIds = newOptions.map((opt) => opt.value).join(',')
        break
      case 'statuses':
        newParams.status = newOptions.map((opt) => opt.value).join(',')
        break
      case 'gateways':
        newParams.gateway = newOptions.map((opt) => opt.value).join(',')
        break
      case 'sources':
        newParams.requestFrom = newOptions.map((opt) => opt.value).join(',')
        break
    }

    setParams(newParams)
    setTriggerFetch(true)
  }

  // Handle timeframe selection
  const handleTimeframeChange = (selectedTimeframe: TimeframeOption | null) => {
    if (!selectedTimeframe) return

    setActiveFilters((prev) => ({
      ...prev,
      selectedTimeframe,
    }))

    if (selectedTimeframe.isCustom) {
      setShowCustomDatePicker(true)
      return
    }

    // Calculate date range based on timeframe
    const now = new Date()
    let startDate: Date

    if (selectedTimeframe.hours) {
      startDate = new Date(now.getTime() - selectedTimeframe.hours * 60 * 60 * 1000)
    } else if (selectedTimeframe.days) {
      startDate = new Date(now.getTime() - selectedTimeframe.days * 24 * 60 * 60 * 1000)
    } else {
      startDate = sevenDaysAgo
    }

    const newDateRange: [Date, Date] = [startDate, now]

    setActiveFilters((prev) => ({
      ...prev,
      dateRange: newDateRange,
    }))

    setParams({
      ...params,
      from: moment(startDate).format('YYYY-MM-DD'),
      to: moment(now).format('YYYY-MM-DD'),
      timeFrame: selectedTimeframe.value,
      page: 1,
    })

    setTriggerFetch(true)
    setShowCustomDatePicker(false)
  }

  // Handle custom date range change
  const handleCustomDateRangeChange = (dates: [Date | null, Date | null]) => {
    setActiveFilters((prev) => ({
      ...prev,
      dateRange: dates,
    }))

    const [startDate, endDate] = dates
    if (startDate && endDate) {
      // Set to custom timeframe when user selects custom dates
      setActiveFilters((prev) => ({
        ...prev,
        selectedTimeframe: timeframeOptions.find((t) => t.isCustom),
      }))

      setParams({
        ...params,
        from: moment(startDate).format('YYYY-MM-DD'),
        to: moment(endDate).format('YYYY-MM-DD'),
        timeFrame: 'custom',
        page: 1,
      })

      setTriggerFetch(true)
    }
  }

  // Handle search text change
  const handleSearchChange = (value: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      searchText: value,
    }))

    setParams({
      ...params,
      sourceDestination: value,
      page: 1,
    })
    setTriggerFetch(true)
  }

  // Handle filter reset
  const handleResetFilters = () => {
    const defaultMessageSource = REQUEST_FROM.filter((el) => el.value !== REQUEST_FORM_TYPE.BULK)

    // Reset active filters
    setActiveFilters({
      users: [],
      statuses: [],
      gateways: [],
      sources: [],
      dateRange: [sevenDaysAgo, today],
      searchText: '',
      selectedTimeframe:
        timeframeOptions.find((t) => t.value === 'last_1_hour') || timeframeOptions.at(0), // Default to 30 days
    })
    setShowCustomDatePicker(false)

    // Reset params
    const resetParams = {
      ...params,
      page: 1,
      requestFrom: defaultMessageSource.map((it) => it.value).join(','),
      status: '',
      userIds: '',
      sourceDestination: '',
      gateway: '',
      from: moment(sevenDaysAgo).format('YYYY-MM-DD'),
      to: moment(today).format('YYYY-MM-DD'),
    }
    setParams(resetParams)

    setTriggerFetch(true)
  }

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (activeFilters.users.length > 0) count++
    if (activeFilters.statuses.length > 0) count++
    if (activeFilters.gateways.length > 0) count++
    if (activeFilters.sources.length > 0) count++
    if (activeFilters.searchText.trim()) count++

    // Count timeframe as active if it's not the default or if it's custom
    if (activeFilters.selectedTimeframe) {
      const isDefaultTimeframe = activeFilters.selectedTimeframe.value === 'last_1_hour'
      if (!isDefaultTimeframe || activeFilters.selectedTimeframe.isCustom) count++
    }

    return count
  }, [activeFilters])

  // Handle export functionality
  const handleExportData = () => {
    toast.info('Export functionality will be implemented here')
    // TODO: Implement export logic
  }

  return (
    <div className='mt-8'>
      <div className='card p-8'>
        <div className='card-header border-0 p-0 align-items-center mb-10'>
          <div className='d-flex flex-column w-100'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bolder fs-2 mb-1'>All Messages</span>
              <span className='text-muted mt-1 fw-bold fs-7'>
                Ordered by most recent • Total Units:{' '}
                <strong>
                  {dataTotal?.data?.items && dataTotal.data.items.length > 0
                    ? dataTotal.data.items[0].total?.toLocaleString()
                    : isFetching
                    ? 'Loading...'
                    : 0}
                </strong>
              </span>
            </h3>

            {/* Enhanced Filter Controls */}
            <div className={`${styles.enhancedFiltersContainer} mt-5`}>
              {/* Filter Header */}
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <div className='d-flex align-items-center gap-3'>
                  <h5 className={`${styles.filterTitle} mb-0`}>
                    <i className='bi bi-funnel me-2 text-primary'></i>
                    Advanced Filters
                  </h5>
                  {activeFilterCount > 0 && (
                    <span className={`badge badge-primary ${styles.filterCountBadge}`}>
                      {activeFilterCount} active
                    </span>
                  )}
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <button
                    type='button'
                    onClick={handleResetFilters}
                    className={`btn ${styles.btnSecondary} ${styles.btnSm}`}
                    disabled={isLoading}
                  >
                    <i className='bi bi-arrow-clockwise me-1'></i>
                    Reset All
                  </button>
                  <button
                    type='button'
                    onClick={handleExportData}
                    className={`btn ${styles.btnPrimary} ${styles.btnSm}`}
                    disabled={isLoading}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    Export
                  </button>
                </div>
              </div>

              {/* Filter Controls */}
              <div className={styles.filterControlsGrid}>
                {/* Date Range Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>
                    <i className='bi bi-calendar3 me-2'></i>
                    Date Range
                  </label>
                  <div className={styles.dateFilterContainer}>
                    {/* Timeframe Selector */}
                    <Select
                      options={timeframeOptions}
                      value={activeFilters.selectedTimeframe}
                      onChange={handleTimeframeChange}
                      placeholder='Select timeframe...'
                      styles={customSelectStyles}
                      isDisabled={isLoading}
                      isClearable={false}
                      getOptionLabel={(option: TimeframeOption) => option.label}
                      getOptionValue={(option: TimeframeOption) => option.value}
                      className={styles.timeframeSelect}
                    />

                    {/* Custom Date Picker - Show when custom is selected */}
                    {showCustomDatePicker && (
                      <div className={`${styles.customDatePicker} mt-2`}>
                        <div className={styles.datePickerWrapper}>
                          <DatePicker
                            selectsRange
                            startDate={activeFilters.dateRange[0]}
                            endDate={activeFilters.dateRange[1]}
                            onChange={handleCustomDateRangeChange}
                            className={`form-control ${styles.datePickerInput}`}
                            placeholderText='Select custom date range'
                            dateFormat='MMM d, yyyy'
                            isClearable
                            disabled={isLoading}
                          />
                          <i className={`bi bi-calendar3 ${styles.dateIcon}`}></i>
                        </div>
                      </div>
                    )}

                    {/* Date Range Display for non-custom selections */}
                    {!showCustomDatePicker &&
                      activeFilters.dateRange[0] &&
                      activeFilters.dateRange[1] && (
                        <div className={`${styles.dateRangeDisplay} mt-2`}>
                          <small className='text-muted'>
                            <i className='bi bi-calendar-range me-1'></i>
                            {moment(activeFilters.dateRange[0]).format('MMM D, YYYY')} -{' '}
                            {moment(activeFilters.dateRange[1]).format('MMM D, YYYY')}
                          </small>
                        </div>
                      )}
                  </div>
                </div>

                {/* Search Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>
                    <i className='bi bi-search me-2'></i>
                    Search
                  </label>
                  <input
                    type='text'
                    className={`form-control ${styles.filterInput}`}
                    placeholder='Shortcode, MSISDN, Mobile, Sender ID'
                    value={activeFilters.searchText}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {/* Users Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>
                    <i className='bi bi-people me-2'></i>
                    Users
                  </label>
                  <Select
                    isMulti
                    options={filterOptions.userOptions}
                    value={activeFilters.users}
                    onChange={(selected, actionMeta) =>
                      handleMultiSelectChange(selected, actionMeta, 'users')
                    }
                    placeholder='Select users...'
                    styles={customSelectStyles}
                    isDisabled={isLoading}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isClearable
                  />
                </div>

                {/* Status Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>
                    <i className='bi bi-check-circle me-2'></i>
                    Status
                  </label>
                  <Select
                    isMulti
                    options={filterOptions.statusOptions}
                    value={activeFilters.statuses}
                    onChange={(selected, actionMeta) =>
                      handleMultiSelectChange(selected, actionMeta, 'statuses')
                    }
                    placeholder='Select status...'
                    styles={customSelectStyles}
                    isDisabled={isLoading}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isClearable
                  />
                </div>

                {/* Gateway Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>
                    <i className='bi bi-router me-2'></i>
                    Gateway
                  </label>
                  <Select
                    isMulti
                    options={filterOptions.gatewayOptions}
                    value={activeFilters.gateways}
                    onChange={(selected, actionMeta) =>
                      handleMultiSelectChange(selected, actionMeta, 'gateways')
                    }
                    placeholder='Select gateways...'
                    styles={customSelectStyles}
                    isDisabled={isLoading}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isClearable
                  />
                </div>

                {/* Message Source Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>
                    <i className='bi bi-diagram-3 me-2'></i>
                    Source
                  </label>
                  <Select
                    isMulti
                    options={filterOptions.sourceOptions}
                    value={activeFilters.sources}
                    onChange={(selected, actionMeta) =>
                      handleMultiSelectChange(selected, actionMeta, 'sources')
                    }
                    placeholder='Select sources...'
                    styles={customSelectStyles}
                    isDisabled={isLoading}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isClearable
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>
                    <i className='bi bi-chat-left me-2'></i>
                    Include Content
                  </label>
                  <ToggleSwitch.Check
                    onChange={() => {
                      setShowContent(!showContent)
                    }}
                    checked={showContent}
                    type='switch'
                    id='custom-switch'
                    className='col-4'
                  />
                </div>
              </div>

              {/* Active Filters Display */}
              {activeFilterCount > 0 && (
                <div className={`${styles.activeFiltersDisplay} mt-4`}>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <span className='text-muted fw-bold me-2'>Active Filters:</span>

                    {activeFilters.users.map((user) => (
                      <span key={`user-${user.value}`} className='badge badge-light-primary'>
                        User: {user.label}
                        <button
                          className='btn btn-sm btn-icon ms-1'
                          onClick={() => {
                            const newUsers = activeFilters.users.filter(
                              (u) => u.value !== user.value
                            )
                            handleMultiSelectChange(
                              newUsers,
                              {action: 'remove-value'} as ActionMeta<SelectOption>,
                              'users'
                            )
                          }}
                        >
                          <i className='bi bi-x fs-8'></i>
                        </button>
                      </span>
                    ))}

                    {activeFilters.statuses.map((status) => (
                      <span key={`status-${status.value}`} className='badge badge-light-success'>
                        Status: {status.label}
                        <button
                          className='btn btn-sm btn-icon ms-1'
                          onClick={() => {
                            const newStatuses = activeFilters.statuses.filter(
                              (s) => s.value !== status.value
                            )
                            handleMultiSelectChange(
                              newStatuses,
                              {action: 'remove-value'} as ActionMeta<SelectOption>,
                              'statuses'
                            )
                          }}
                        >
                          <i className='bi bi-x fs-8'></i>
                        </button>
                      </span>
                    ))}

                    {activeFilters.gateways.map((gateway) => (
                      <span key={`gateway-${gateway.value}`} className='badge badge-light-info'>
                        Gateway: {gateway.label}
                        <button
                          className='btn btn-sm btn-icon ms-1'
                          onClick={() => {
                            const newGateways = activeFilters.gateways.filter(
                              (g) => g.value !== gateway.value
                            )
                            handleMultiSelectChange(
                              newGateways,
                              {action: 'remove-value'} as ActionMeta<SelectOption>,
                              'gateways'
                            )
                          }}
                        >
                          <i className='bi bi-x fs-8'></i>
                        </button>
                      </span>
                    ))}

                    {activeFilters.sources.map((source) => (
                      <span key={`source-${source.value}`} className='badge badge-light-warning'>
                        Source: {source.label}
                        <button
                          className='btn btn-sm btn-icon ms-1'
                          onClick={() => {
                            const newSources = activeFilters.sources.filter(
                              (s) => s.value !== source.value
                            )
                            handleMultiSelectChange(
                              newSources,
                              {action: 'remove-value'} as ActionMeta<SelectOption>,
                              'sources'
                            )
                          }}
                        >
                          <i className='bi bi-x fs-8'></i>
                        </button>
                      </span>
                    ))}

                    {activeFilters.searchText && (
                      <span className='badge badge-light-dark'>
                        Search: "{activeFilters.searchText}"
                        <button
                          className='btn btn-sm btn-icon ms-1'
                          onClick={() => handleSearchChange('')}
                        >
                          <i className='bi bi-x fs-8'></i>
                        </button>
                      </span>
                    )}

                    {activeFilters.selectedTimeframe &&
                      (activeFilters.selectedTimeframe.value !== 'last_1_hour' ||
                        activeFilters.selectedTimeframe.isCustom) && (
                        <span className='badge badge-light-secondary'>
                          <i className='bi bi-clock me-1'></i>
                          {activeFilters.selectedTimeframe.label}
                          <button
                            className='btn btn-sm btn-icon ms-1'
                            onClick={() => {
                              const defaultTimeframe = timeframeOptions.find(
                                (t) => t.value === 'last_1_hour'
                              )
                              if (defaultTimeframe) {
                                handleTimeframeChange(defaultTimeframe)
                              }
                            }}
                          >
                            <i className='bi bi-x fs-8'></i>
                          </button>
                        </span>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div
            className='d-flex justify-content-center align-items-center'
            style={{minHeight: '300px'}}
          >
            <div className='text-center'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
              <div className='mt-3 text-muted'>Loading messages...</div>
            </div>
          </div>
        ) : (
          <>
            {data?.data.items && data.data.items.length > 0 ? (
              <div className='card-body p-0 pt-1'>
                {/* Summary Info */}
                <div className='d-flex justify-content-between align-items-center mb-5 px-3'>
                  <div className='text-muted'>
                    Showing <strong>{loadedData.startLoad}</strong> to{' '}
                    <strong>{loadedData.endLoad}</strong> of{' '}
                    <strong>{data.data.total ? data.data.total.toLocaleString() : 0}</strong>{' '}
                    entries
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    <span className='text-muted fw-bold'>Show:</span>
                    <select
                      className='form-select form-select-sm w-auto'
                      onChange={(e) => {
                        setParams({...params, limit: parseInt(e.target.value), page: 1})
                        setTriggerFetch(true)
                      }}
                      value={params.limit || 10}
                      disabled={isFetching}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className='text-muted fw-bold'>entries</span>
                  </div>
                </div>

                <div className='table-responsive'>
                  <table className={`table align-middle gs-0 gy-5 ${styles.allMessageTable}`}>
                    <thead>
                      <tr className='fw-bold text-muted bg-light'>
                        <th className='p-3 min-w-100px'>Message ID</th>
                        <th className='p-3 min-w-120px'>Medium</th>
                        <th className='p-3 min-w-120px'>Source</th>
                        <th className='p-3 min-w-150px'>Date/Time</th>
                        <th className='p-3 min-w-80px'>Origin</th>
                        <th className='p-3 min-w-120px'>From</th>
                        <th className='p-3 min-w-120px'>To</th>
                        {showContent && <th className='p-3 min-w-200px'>Message</th>}
                        <th className='p-3 min-w-200px'>Status Timeline</th>
                        <th className='p-3 min-w-80px text-center'>Units</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isFetching && (
                        <tr>
                          <td colSpan={9} className='text-center py-5'>
                            <div className='d-flex justify-content-center align-items-center'>
                              <div
                                className='spinner-border spinner-border-sm text-primary me-2'
                                role='status'
                              >
                                <span className='visually-hidden'>Loading...</span>
                              </div>
                              <span className='text-muted'>Updating...</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {data?.data?.items?.map((item, i: number) => (
                        <tr key={item._id || i} className='border-bottom border-gray-200'>
                          <td className='p-3'>
                            <div className='d-flex flex-column'>
                              <span className='text-dark fw-bold fs-7 font-monospace'>
                                {item._id}
                              </span>
                            </div>
                          </td>
                          <td className='p-3'>
                            <div className='d-flex flex-column'>
                              <span className='text-dark fw-bold fs-7 font-monospace'>
                                {item?.medium}
                              </span>
                            </div>
                          </td>
                          <td className='p-3'>
                            <div className='d-flex align-items-center'>
                              <div className='symbol symbol-40px me-3'>
                                <div className='symbol-label bg-light-primary'>
                                  <i className='bi bi-chat-text text-primary fs-4'></i>
                                </div>
                              </div>
                              <div className='d-flex flex-column'>
                                <span className='text-dark fw-bold'>
                                  {item.request_name || 'Quick Message'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className='p-3'>
                            <div className='d-flex flex-column'>
                              <span className='text-dark fw-bold'>
                                {moment(item?.scheduleAt || item?.createdAt).format('MMM DD, YYYY')}
                              </span>
                              <span className='text-muted fs-7'>
                                {moment(item?.scheduleAt || item?.createdAt).format('hh:mm A')}
                              </span>
                            </div>
                          </td>
                          <td className='p-3'>
                            <span
                              className={`badge badge-light-${
                                item?.campaign_type === 'reply' ? 'info' : 'primary'
                              } fw-bold`}
                            >
                              {item?.campaign_type === 'reply' ? 'MO' : 'MT'}
                            </span>
                          </td>
                          <td className='p-3'>
                            <span className='text-dark fw-bold font-monospace fs-7'>
                              {item?.sender || '-'}
                            </span>
                          </td>
                          <td className='p-3'>
                            <span className='text-dark fw-bold font-monospace fs-7'>
                              {item?.receiver || '-'}
                            </span>
                          </td>
                          {showContent && (
                            <td className='p-3'>
                              <div className='d-flex flex-column'>
                                <div
                                  className='text-dark message-preview'
                                  style={{maxWidth: '200px'}}
                                >
                                  {item?.message ? (
                                    item.message.length > 50 ? (
                                      <>
                                        <div id={`msg-row-${item._id}`}>
                                          <span>{item.message.substring(0, 50)}...</span>
                                          <button
                                            className='btn btn-link btn-sm p-0 ms-1 text-primary'
                                            onClick={() => {
                                              // Toggle full message display
                                              const element = document.getElementById(
                                                `msg-${item._id}`
                                              )
                                              if (element) {
                                                element.style.display =
                                                  element.style.display === 'none'
                                                    ? 'block'
                                                    : 'none'
                                              }

                                              const elementRow = document.getElementById(
                                                `msg-row-${item._id}`
                                              )
                                              if (elementRow) {
                                                elementRow.style.display = 'none'
                                              }
                                            }}
                                          >
                                            Show More
                                          </button>
                                        </div>
                                        <div
                                          id={`msg-${item._id}`}
                                          style={{display: 'none'}}
                                          className='mt-2 p-2 bg-light rounded'
                                        >
                                          {item.message}

                                          <button
                                            className='btn btn-link btn-sm p-0 ms-1 text-primary'
                                            onClick={() => {
                                              // Toggle full message display
                                              const element = document.getElementById(
                                                `msg-${item._id}`
                                              )
                                              if (element) {
                                                element.style.display =
                                                  element.style.display === 'none'
                                                    ? 'block'
                                                    : 'none'
                                              }
                                              const elementRow = document.getElementById(
                                                `msg-row-${item._id}`
                                              )
                                              if (elementRow) {
                                                elementRow.style.display = 'block'
                                              }
                                            }}
                                          >
                                            Show Less
                                          </button>
                                        </div>
                                      </>
                                    ) : (
                                      item.message
                                    )
                                  ) : (
                                    '-'
                                  )}
                                </div>
                              </div>
                            </td>
                          )}
                          <td className='p-3'>
                            <div className='d-flex flex-column gap-1'>
                              <div className='d-flex align-items-center'>
                                <span className='badge badge-light-warning badge-sm me-2'>
                                  QUEUED
                                </span>
                                <span className='text-muted fs-8'>
                                  {item?.createdAt
                                    ? moment(item.createdAt).format('MM/DD HH:mm')
                                    : '-'}
                                </span>
                              </div>
                              {item?.send_time && (
                                <div className='d-flex align-items-center'>
                                  <span className='badge badge-light-info badge-sm me-2'>SENT</span>
                                  <span className='text-muted fs-8'>
                                    {moment(item.send_time).format('MM/DD HH:mm')}
                                  </span>
                                </div>
                              )}
                              {item?.response_time && (
                                <div className='d-flex align-items-center'>
                                  <span className='badge badge-light-success badge-sm me-2'>
                                    DELIVERED
                                  </span>
                                  <span className='text-muted fs-8'>
                                    {moment(item.response_time).format('MM/DD HH:mm')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className='p-3 text-center'>
                            <span className='badge badge-light-dark fw-bold'>
                              {item.smsDetail && item.smsDetail.smscount
                                ? item.smsDetail.smscount
                                : '--'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Improved Pagination */}
                <div className='d-flex justify-content-between align-items-center mt-5 px-3'>
                  <div className='text-muted'>
                    {isFetching ? (
                      <span className='d-flex align-items-center'>
                        <div className='spinner-border spinner-border-sm me-2' role='status'>
                          <span className='visually-hidden'>Loading...</span>
                        </div>
                        Updating...
                      </span>
                    ) : (
                      `Showing ${loadedData.startLoad} - ${loadedData.endLoad} of ${
                        data.data.total ? data.data.total.toLocaleString() : 0
                      } Messages`
                    )}
                  </div>

                  {data?.data?.totalPages && data.data.totalPages > 1 && (
                    <div className='d-flex align-items-center gap-2'>
                      <button
                        className='btn btn-sm btn-light'
                        onClick={() => {
                          setParams({...params, page: Math.max(1, params.page - 1)})
                          setTriggerFetch(true)
                        }}
                        disabled={params.page <= 1 || isFetching}
                      >
                        <i className='bi bi-chevron-left'></i>
                        Previous
                      </button>

                      <div className='d-flex align-items-center gap-1'>
                        {/* Page numbers */}
                        {(() => {
                          const totalPages = data.data.totalPages
                          const currentPage = params.page
                          const pages = []

                          // Calculate start and end page numbers
                          let startPage = Math.max(1, currentPage - 2)
                          let endPage = Math.min(totalPages, currentPage + 2)

                          // Adjust if we're near the beginning or end
                          if (endPage - startPage < 4) {
                            if (startPage === 1) {
                              endPage = Math.min(totalPages, startPage + 4)
                            } else if (endPage === totalPages) {
                              startPage = Math.max(1, endPage - 4)
                            }
                          }

                          // Add first page and ellipsis if needed
                          if (startPage > 1) {
                            pages.push(
                              <button
                                key={1}
                                className={`btn btn-sm ${
                                  1 === currentPage ? 'btn-primary' : 'btn-light'
                                }`}
                                onClick={() => {
                                  setParams({...params, page: 1})
                                  setTriggerFetch(true)
                                }}
                                disabled={isFetching}
                              >
                                1
                              </button>
                            )
                            if (startPage > 2) {
                              pages.push(
                                <span key='ellipsis1' className='px-2 text-muted'>
                                  ...
                                </span>
                              )
                            }
                          }

                          // Add page numbers
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                className={`btn btn-sm ${
                                  i === currentPage ? 'btn-primary' : 'btn-light'
                                }`}
                                onClick={() => {
                                  setParams({...params, page: i})
                                  setTriggerFetch(true)
                                }}
                                disabled={isFetching}
                              >
                                {i}
                              </button>
                            )
                          }

                          // Add ellipsis and last page if needed
                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <span key='ellipsis2' className='px-2 text-muted'>
                                  ...
                                </span>
                              )
                            }
                            pages.push(
                              <button
                                key={totalPages}
                                className={`btn btn-sm ${
                                  totalPages === currentPage ? 'btn-primary' : 'btn-light'
                                }`}
                                onClick={() => {
                                  setParams({...params, page: totalPages})
                                  setTriggerFetch(true)
                                }}
                                disabled={isFetching}
                              >
                                {totalPages}
                              </button>
                            )
                          }

                          return pages
                        })()}
                      </div>

                      <button
                        className='btn btn-sm btn-light'
                        onClick={() => {
                          setParams({
                            ...params,
                            page: Math.min(data.data.totalPages, params.page + 1),
                          })
                          setTriggerFetch(true)
                        }}
                        disabled={params.page >= data.data.totalPages || isFetching}
                      >
                        Next
                        <i className='bi bi-chevron-right'></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                className='d-flex justify-content-center align-items-center flex-column'
                style={{minHeight: '300px'}}
              >
                <div className='text-center'>
                  <i className='bi bi-inbox fs-1 text-muted mb-3'></i>
                  <h5 className='text-muted'>No messages found</h5>
                  <p className='text-muted'>Try adjusting your filters or date range</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default AdminActivityLogsPage
